import React from 'react';

/**
 * UnifiedTable — semantic table primitives for the platform.
 *
 * Usage:
 *  <UnifiedTable testId="students-table">
 *    <UnifiedTableHead>
 *      <UnifiedTableRow>
 *        <UnifiedTableHeader>الاسم</UnifiedTableHeader>
 *        ...
 *      </UnifiedTableRow>
 *    </UnifiedTableHead>
 *    <UnifiedTableBody>
 *      <UnifiedTableRow>
 *        <UnifiedTableCell>أحمد</UnifiedTableCell>
 *      </UnifiedTableRow>
 *    </UnifiedTableBody>
 *  </UnifiedTable>
 */
export const UnifiedTable = ({ children, className = '', testId, style }) => (
  <div
    className={`mesp-ui ${className}`}
    style={{
      width: '100%',
      overflowX: 'auto',
      borderRadius: 'var(--mesp-radius-lg)',
      border: '1px solid var(--mesp-border)',
      background: 'var(--mesp-bg-elevated)',
      ...style,
    }}
    data-testid={testId}
  >
    <table
      style={{
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        fontSize: 'var(--mesp-font-body)',
        color: 'var(--mesp-text)',
      }}
    >
      {children}
    </table>
  </div>
);

export const UnifiedTableHead = ({ children }) => (
  <thead style={{ background: 'var(--mesp-bg-sunken)' }}>
    {children}
  </thead>
);

export const UnifiedTableBody = ({ children }) => <tbody>{children}</tbody>;

export const UnifiedTableRow = ({ children, onClick, className = '', testId, hoverable = true }) => (
  <tr
    onClick={onClick}
    data-testid={testId}
    className={className}
    style={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background var(--mesp-trans-fast)',
    }}
    onMouseEnter={(e) => {
      if (hoverable) e.currentTarget.style.background = 'var(--mesp-gold-soft-2)';
    }}
    onMouseLeave={(e) => {
      if (hoverable) e.currentTarget.style.background = 'transparent';
    }}
  >
    {children}
  </tr>
);

export const UnifiedTableHeader = ({ children, align = 'start', className = '', style }) => (
  <th
    className={className}
    style={{
      textAlign: align,
      padding: '0.75rem 1rem',
      fontSize: 'var(--mesp-font-xs)',
      fontWeight: 'var(--mesp-font-weight-semibold)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: 'var(--mesp-text-muted)',
      borderBottom: '1px solid var(--mesp-border)',
      whiteSpace: 'nowrap',
      ...style,
    }}
  >
    {children}
  </th>
);

export const UnifiedTableCell = ({ children, align = 'start', className = '', style, colSpan }) => (
  <td
    colSpan={colSpan}
    className={className}
    style={{
      textAlign: align,
      padding: '0.75rem 1rem',
      borderBottom: '1px solid var(--mesp-divider)',
      color: 'var(--mesp-text)',
      verticalAlign: 'middle',
      ...style,
    }}
  >
    {children}
  </td>
);

export default UnifiedTable;
